import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const PREDEFINED_COLORS = [
  { name: 'Azul', value: '#2563EB' },
  { name: 'Ciano', value: '#0891B2' },
  { name: 'Verde', value: '#059669' },
  { name: 'Lima', value: '#65A30D' },
  { name: 'Ambar', value: '#D97706' },
  { name: 'Laranja', value: '#EA580C' },
  { name: 'Vermelho', value: '#DC2626' },
  { name: 'Rosa', value: '#DB2777' },
  { name: 'Fucsia', value: '#C026D3' },
  { name: 'Violeta', value: '#7C3AED' },
  { name: 'Indigo', value: '#4F46E5' },
  { name: 'Slate', value: '#475569' },
];

const COMMON_ICONS = [
  '📋', '🎯', '🚀', '⚡', '🔥', '💡', '🏁', '📌',
  '🧩', '🛠️', '📈', '📝', '🔔', '⭐', '💎', '🎨',
  '🧪', '📦', '🎉', '🏆', '🌊', '🛰️', '🔒', '🎪',
];

function getDefaultDraft(group, customization) {
  return {
    color: customization?.color || group?.color || '#2563EB',
    icon: customization?.icon || '',
    collapsed: customization?.collapsed || false,
  };
}

function countCustomizationFields(customization, group) {
  if (!customization) return 0;
  let count = 0;
  if ((customization.color || '') !== (group?.color || '')) count += 1;
  if (customization.icon) count += 1;
  if (customization.collapsed) count += 1;
  return count;
}

function normalizeEmojiValue(value) {
  return Array.from(value || '').slice(0, 2).join('');
}

export default function ProjectGroupCustomization({ board, groups, api, onDataChange, isOpen, onClose }) {
  const [groupCustomizations, setGroupCustomizations] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [draft, setDraft] = useState({ color: '#2563EB', icon: '', collapsed: false });

  const selectedGroup = useMemo(
    () => (groups || []).find(group => group.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );
  const selectedCustomization = selectedGroup ? groupCustomizations[selectedGroup.id] : null;
  const customizedGroupsCount = Object.keys(groupCustomizations).length;

  useEffect(() => {
    if (!isOpen || !board) return;
    fetchGroupCustomizations();
  }, [isOpen, board]);

  useEffect(() => {
    if (!isOpen) return;
    if (!groups?.length) {
      setSelectedGroupId('');
      return;
    }
    setSelectedGroupId(current => current || groups[0].id);
  }, [groups, isOpen]);

  useEffect(() => {
    if (!selectedGroup) return;
    setDraft(getDefaultDraft(selectedGroup, groupCustomizations[selectedGroup.id]));
  }, [selectedGroup, groupCustomizations]);

  const fetchGroupCustomizations = async () => {
    try {
      const response = await api.get(`/projects/boards/${board.id}/group-customizations`);
      const nextCustomizations = {};
      (response.data || []).forEach(customization => {
        nextCustomizations[customization.group_id] = customization;
      });
      setGroupCustomizations(nextCustomizations);
    } catch (error) {
      console.error('Error fetching group customizations:', error);
      toast.error('Erro ao carregar personalizacoes dos grupos');
    }
  };

  const applyDraft = (patch) => {
    setDraft(current => ({ ...current, ...patch }));
  };

  const saveCustomization = async () => {
    if (!selectedGroup) return;

    setLoading(true);
    try {
      const customizationData = {
        group_id: selectedGroup.id,
        board_id: board.id,
        color: draft.color,
        icon: draft.icon.trim(),
        collapsed: draft.collapsed,
      };

      await api.post(`/projects/boards/${board.id}/group-customizations`, customizationData);
      setGroupCustomizations(current => ({
        ...current,
        [selectedGroup.id]: customizationData,
      }));
      toast.success('Personalizacao do grupo salva');
      onDataChange?.();
    } catch (error) {
      console.error('Error saving group customization:', error);
      toast.error('Erro ao salvar personalizacao do grupo');
    } finally {
      setLoading(false);
    }
  };

  const resetCustomization = async () => {
    if (!selectedGroup) return;

    try {
      await api.delete(`/projects/boards/${board.id}/group-customizations/${selectedGroup.id}`);
      setGroupCustomizations(current => {
        const next = { ...current };
        delete next[selectedGroup.id];
        return next;
      });
      setDraft(getDefaultDraft(selectedGroup, null));
      toast.success('Personalizacao removida');
      onDataChange?.();
    } catch (error) {
      console.error('Error resetting customization:', error);
      toast.error('Erro ao remover personalizacao');
    }
  };

  const hasSavedCustomization = Boolean(selectedGroup && groupCustomizations[selectedGroup.id]);
  const hasChanges = useMemo(() => {
    if (!selectedGroup) return false;
    const baseline = getDefaultDraft(selectedGroup, selectedCustomization);
    return baseline.color !== draft.color || baseline.icon !== draft.icon || baseline.collapsed !== draft.collapsed;
  }, [draft, selectedCustomization, selectedGroup]);

  const previewBadgeTone = draft.collapsed
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-emerald-200 bg-emerald-50 text-emerald-700';

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[1180px] overflow-y-auto lg:overflow-hidden border-slate-200 p-0">
        <DialogHeader className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.12),_transparent_32%),linear-gradient(135deg,_#f8fafc,_#ffffff_58%,_#eff6ff)] px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <DialogTitle className="text-2xl font-semibold text-slate-900">Personalizacao de grupos</DialogTitle>
              <p className="mt-2 text-sm text-slate-500">
                Ajuste cor, icone e estado inicial dos grupos com o mesmo padrao visual do restante do Nexo Project.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="rounded-full border-blue-200 bg-blue-50 px-3 py-1 text-blue-700">
                {(groups || []).length} grupos no quadro
              </Badge>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-white px-3 py-1 text-slate-700">
                {customizedGroupsCount} com personalizacao salva
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="grid lg:max-h-[84vh] lg:min-h-[680px] lg:overflow-hidden lg:grid-cols-[360px_minmax(0,1fr)]">
          <section className="flex min-h-0 flex-col border-r border-slate-200 bg-slate-50/70">
            <div className="border-b border-slate-200 px-6 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Mapa de grupos</p>
              <h3 className="mt-1 text-xl font-semibold text-slate-900">Selecione onde aplicar o ajuste</h3>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-3">
                {(groups || []).map((group, index) => {
                  const customization = groupCustomizations[group.id];
                  const displayColor = customization?.color || group.color || '#2563EB';
                  const displayIcon = customization?.icon || '';
                  const customizationCount = countCustomizationFields(customization, group);
                  const isSelected = selectedGroupId === group.id;

                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => setSelectedGroupId(group.id)}
                      className={`w-full rounded-3xl border p-5 text-left transition-colors ${
                        isSelected
                          ? 'border-blue-200 bg-white shadow-sm ring-2 ring-blue-500/15'
                          : 'border-slate-200 bg-white/80 hover:border-slate-300 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="h-3.5 w-3.5 rounded-full shrink-0" style={{ backgroundColor: displayColor }} />
                            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${displayIcon ? 'border-slate-200 bg-slate-50 text-2xl' : 'border-dashed border-slate-200 bg-white text-slate-300'}`}>
                              {displayIcon || '○'}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-900">{group.title}</p>
                              <p className="mt-1 text-xs text-slate-500">Cor, icone e estado inicial</p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                              Grupo {index + 1}
                            </span>
                            {customization ? (
                              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700">
                                {customizationCount} ajuste(s)
                              </span>
                            ) : (
                              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500">
                                Padrao do quadro
                              </span>
                            )}
                            {customization?.collapsed && (
                              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                                Recolhido
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {!groups?.length && (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                    Nenhum grupo disponivel para personalizar neste quadro.
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="flex min-h-0 flex-col bg-white">
            {!selectedGroup ? (
              <div className="flex h-full items-center justify-center px-8 text-center">
                <div>
                  <div className="text-4xl">🎨</div>
                  <h3 className="mt-4 text-xl font-semibold text-slate-900">Selecione um grupo</h3>
                  <p className="mt-2 text-sm text-slate-500">
                    O editor aparece aqui com visualizacao, presets e estado inicial do grupo.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="border-b border-slate-200 px-6 py-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Editor do grupo</p>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: draft.color }} />
                        {draft.icon ? <span className="text-xl">{draft.icon}</span> : null}
                        <h3 className="truncate text-2xl font-semibold text-slate-900">{selectedGroup.title}</h3>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-xs font-medium ${previewBadgeTone}`}>
                          {draft.collapsed ? 'Abre recolhido' : 'Abre expandido'}
                        </span>
                        {hasSavedCustomization && (
                          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                            Personalizacao salva
                          </span>
                        )}
                        {hasChanges && (
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                            Alteracoes pendentes
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {hasSavedCustomization && (
                        <Button type="button" variant="outline" onClick={resetCustomization}>
                          Resetar
                        </Button>
                      )}
                      <Button type="button" variant="outline" onClick={onClose}>
                        Fechar
                      </Button>
                      <Button type="button" onClick={saveCustomization} disabled={loading || !hasChanges}>
                        {loading ? 'Salvando...' : 'Salvar personalizacao'}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid min-h-0 flex-1 gap-0 2xl:grid-cols-[1.02fr_0.98fr]">
                  <div className="min-h-0 overflow-y-auto px-6 py-5 2xl:border-r 2xl:border-slate-200">
                    <div className="space-y-6">
                      <section className="rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.14),_transparent_34%),linear-gradient(135deg,_#0f172a,_#172554_58%,_#1d4ed8)] p-6 text-white shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-200">Preview ao vivo</p>
                        <div className="mt-4 rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex min-w-0 items-center gap-4">
                              <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: draft.color }} />
                              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl border border-white/10 bg-white/10 text-3xl">
                                {draft.icon || '•'}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-xl font-semibold">{selectedGroup.title}</p>
                                <p className="mt-1 text-sm text-slate-200">
                                  {draft.collapsed ? 'Este grupo abre recolhido por padrao.' : 'Este grupo abre expandido e visivel.'}
                                </p>
                              </div>
                            </div>
                            <span className="self-start rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium lg:self-auto">
                              {draft.collapsed ? 'Recolhido' : 'Expandido'}
                            </span>
                          </div>
                        </div>
                      </section>

                      <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                        <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Cor do grupo</Label>
                        <div className="mt-4 flex items-center gap-3">
                          <input
                            type="color"
                            value={draft.color}
                            onChange={(event) => applyDraft({ color: event.target.value })}
                            className="h-11 w-14 rounded-xl border border-slate-200 bg-white"
                          />
                          <Input
                            value={draft.color}
                            onChange={(event) => applyDraft({ color: event.target.value })}
                            placeholder="#2563EB"
                            className="h-11 rounded-xl border-slate-200 bg-white"
                          />
                        </div>

                        <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-6 xl:grid-cols-4 2xl:grid-cols-6">
                          {PREDEFINED_COLORS.map(color => (
                            <button
                              key={color.value}
                              type="button"
                              title={color.name}
                              onClick={() => applyDraft({ color: color.value })}
                              className={`h-12 rounded-3xl border-2 transition-transform hover:scale-[1.03] ${
                                draft.color === color.value ? 'border-slate-900 shadow-sm' : 'border-white'
                              }`}
                              style={{ backgroundColor: color.value }}
                            />
                          ))}
                        </div>
                      </section>

                      <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Icone do grupo</Label>
                            <p className="mt-1 text-sm text-slate-500">
                              Use um emoji para diferenciar areas, fases ou temas.
                            </p>
                          </div>
                          {draft.icon && (
                            <Button type="button" variant="outline" size="sm" onClick={() => applyDraft({ icon: '' })}>
                              Limpar icone
                            </Button>
                          )}
                        </div>

                        <Input
                          value={draft.icon}
                          onChange={(event) => applyDraft({ icon: normalizeEmojiValue(event.target.value) })}
                          placeholder="Digite um emoji ou escolha abaixo"
                          className="mt-4 h-11 rounded-xl border-slate-200 bg-white"
                        />

                        <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5 xl:grid-cols-4 2xl:grid-cols-6">
                          {COMMON_ICONS.map(icon => (
                            <button
                              key={icon}
                              type="button"
                              onClick={() => applyDraft({ icon })}
                              className={`flex h-14 items-center justify-center rounded-3xl border transition-colors ${
                                draft.icon === icon
                                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 bg-white hover:bg-slate-50'
                              }`}
                            >
                              <span className="text-3xl leading-none">{icon}</span>
                            </button>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Comportamento inicial</Label>
                            <p className="mt-1 text-sm text-slate-500">
                              Defina se o grupo deve aparecer recolhido quando a board abrir.
                            </p>
                          </div>
                          <Switch checked={draft.collapsed} onCheckedChange={(checked) => applyDraft({ collapsed: checked })} />
                        </div>
                      </section>
                    </div>
                  </div>

                  <div className="min-h-0 overflow-y-auto border-t border-slate-200 px-6 py-5 2xl:border-t-0">
                    <div className="space-y-6">
                      <section className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                        <Tabs defaultValue="resumo" className="w-full">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Painel auxiliar</p>
                              <h4 className="mt-1 text-lg font-semibold text-slate-900">Resumo e diretrizes</h4>
                            </div>
                            <TabsList className="grid w-full grid-cols-2 rounded-2xl bg-white lg:w-[280px]">
                              <TabsTrigger value="resumo" className="rounded-2xl">Resumo</TabsTrigger>
                              <TabsTrigger value="guia" className="rounded-2xl">Boas praticas</TabsTrigger>
                            </TabsList>
                          </div>

                          <TabsContent value="resumo" className="mt-5 space-y-4">
                            <Card className="rounded-3xl border-slate-200 shadow-none">
                              <CardContent className="p-5">
                                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Cor aplicada</p>
                                <div className="mt-4 flex items-center gap-4">
                                  <span className="h-12 w-12 shrink-0 rounded-3xl border border-slate-200" style={{ backgroundColor: draft.color }} />
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900">{draft.color}</p>
                                    <p className="mt-1 text-sm text-slate-500">Token visual principal do grupo.</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="rounded-3xl border-slate-200 shadow-none">
                              <CardContent className="p-5">
                                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Icone</p>
                                <div className="mt-4 flex items-center gap-4">
                                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl border border-slate-200 bg-white text-3xl leading-none">
                                    {draft.icon || '—'}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900">{draft.icon || 'Sem icone'}</p>
                                    <p className="mt-1 text-sm text-slate-500">Elemento de reconhecimento rapido do grupo.</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="rounded-3xl border-slate-200 shadow-none">
                              <CardContent className="p-5">
                                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Estado inicial</p>
                                <div className="mt-4">
                                  <p className="text-lg font-semibold text-slate-900">{draft.collapsed ? 'Recolhido' : 'Expandido'}</p>
                                  <p className="mt-2 text-sm text-slate-500">
                                    {draft.collapsed ? 'Bom para backlog, grupos de apoio e areas menos acessadas.' : 'Bom para grupos ativos e de acompanhamento diario.'}
                                  </p>
                                </div>
                              </CardContent>
                            </Card>
                          </TabsContent>

                          <TabsContent value="guia" className="mt-5">
                            <div className="space-y-3">
                              <Card className="rounded-3xl border-slate-200 shadow-none">
                                <CardContent className="p-5">
                                  <p className="text-sm font-semibold text-slate-900">Cores</p>
                                  <p className="mt-2 text-sm leading-6 text-slate-600">
                                    Use cores consistentes entre fases parecidas para reduzir ruido visual e facilitar leitura transversal do board.
                                  </p>
                                </CardContent>
                              </Card>

                              <Card className="rounded-3xl border-slate-200 shadow-none">
                                <CardContent className="p-5">
                                  <p className="text-sm font-semibold text-slate-900">Icones</p>
                                  <p className="mt-2 text-sm leading-6 text-slate-600">
                                    Reserve icones para destacar grupos estrategicos, frentes especiais ou blocos que precisam reconhecimento imediato.
                                  </p>
                                </CardContent>
                              </Card>

                              <Card className="rounded-3xl border-slate-200 shadow-none">
                                <CardContent className="p-5">
                                  <p className="text-sm font-semibold text-slate-900">Colapso inicial</p>
                                  <p className="mt-2 text-sm leading-6 text-slate-600">
                                    Recolha grupos de backlog ou baixa prioridade para preservar foco no fluxo atual e evitar poluicao visual.
                                  </p>
                                </CardContent>
                              </Card>
                            </div>
                          </TabsContent>
                        </Tabs>
                      </section>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
